import {
  QueryObserver,
  QueryObserverOptions,
  queryOptions,
  skipToken,
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Hash, WalletCallReceipt } from 'viem'
import { base } from 'viem/chains'
import { Config, useChainId, useConfig } from 'wagmi'
import { getCallsStatus } from 'wagmi/actions'
import {
  WaitForTransactionReceiptData,
  waitForTransactionReceiptQueryOptions as wagmi_waitForTransactionReceiptQueryOptions,
} from 'wagmi/query'

import { ensureBlockSyncQueryOptions, useEnsureBlockSync } from './useEnsureBlockSync'

type TransactionType = 'regular' | 'eip-5792'
type UseWaitForTransactionReceiptUniversalParams<T extends TransactionType> = T extends 'regular'
  ? { type: T; hash: Hash | undefined }
  : { type: T; callsId: string | undefined }
type CallsStatusData =
  | {
      status: 'pending'
    }
  | {
      status: 'success'
      receipts: WalletCallReceipt<bigint, 'success'>[]
    }
  | {
      status: 'reverted'
      error: Error
    }
type UseWaitForTransactionReceiptUniversalResult<T extends TransactionType> = T extends 'regular'
  ? UseQueryResult<WaitForTransactionReceiptData<Config, number>>
  : UseQueryResult<CallsStatusData>

export function useWaitForTransactionReceiptUniversal<T extends TransactionType>(
  args: UseWaitForTransactionReceiptUniversalParams<T>,
  options: {
    onSettledUnmounted?: () => void
  } = {},
): UseWaitForTransactionReceiptUniversalResult<T> {
  const chainId = useChainId()
  const wagmiConfig = useConfig()
  const queryClient = useQueryClient()
  const isMounted = useIsMounted()
  const onSettledUnmounted = useRef<() => void>()
  useEffect(() => {
    onSettledUnmounted.current = options.onSettledUnmounted
  }, [options.onSettledUnmounted])
  const isBlockSyncEnabled = import.meta.env.VITE_PLAYWRIGHT !== '1' && chainId === base.id

  const hash = args.type === 'regular' ? args.hash : undefined
  const regularTransactionQueryOptions = useMemo(
    () =>
      waitForTransactionReceiptQueryOptions(wagmiConfig, {
        hash,
        chainId,
      }),

    [wagmiConfig, chainId, hash],
  )

  const callsId = args.type === 'eip-5792' ? args.callsId : undefined
  const eip5792TransactionQueryOptions = useMemo(
    () =>
      callsStatusQueryOptions(wagmiConfig, {
        callsId,
      }),
    [wagmiConfig, callsId],
  )

  const queryOptions = useMemo(
    () => (args.type === 'regular' ? regularTransactionQueryOptions : eip5792TransactionQueryOptions),
    [args.type, regularTransactionQueryOptions, eip5792TransactionQueryOptions],
  )
  const defaultedOptions = useMemo(
    () => queryClient.defaultQueryOptions(queryOptions as QueryObserverOptions),
    [queryOptions, queryClient.defaultQueryOptions],
  )

  useEffect(() => {
    if (!defaultedOptions.enabled) {
      return
    }
    const observer = new QueryObserver(queryClient, defaultedOptions)

    const unsubscribe = observer.subscribe((data) => {
      if (data.status === 'pending') {
        return
      }
      const isEip5792 = defaultedOptions.queryKey[0] === CALLS_STATUS_QUERY_KEY_PREFIX
      if (isEip5792 && (data.data as CallsStatusData)?.status === 'pending') {
        return
      }

      setTimeout(async () => {
        if (!isMounted()) {
          if (isBlockSyncEnabled) {
            const targetBlock = (() => {
              if (isEip5792) {
                const callsStatusData = data.data as CallsStatusData
                if (callsStatusData.status === 'success') {
                  return callsStatusData.receipts?.[0]?.blockNumber
                }
                return undefined
              }
              const regularTransactionData = data.data as WaitForTransactionReceiptData<Config, number>
              return regularTransactionData.blockNumber
            })()
            await queryClient
              .fetchQuery(ensureBlockSyncQueryOptions(wagmiConfig, { targetBlock, enabled: true }))
              .catch(() => {})
          }
          onSettledUnmounted.current?.()
        }
      }, 500)
      queueMicrotask(() => unsubscribe())
    })
  }, [defaultedOptions, isMounted, queryClient, isBlockSyncEnabled, wagmiConfig])

  const regularTransactionQuery = useQuery(regularTransactionQueryOptions)

  const eip5792TransactionQuery = useQuery(eip5792TransactionQueryOptions)

  const queryStatus = args.type === 'regular' ? regularTransactionQuery.status : eip5792TransactionQuery.status

  const blockSync = useEnsureBlockSync({
    targetBlock: (() => {
      if (args.type === 'regular') {
        return regularTransactionQuery.data?.blockNumber
      }
      if (args.type === 'eip-5792' && eip5792TransactionQuery.data?.status === 'success') {
        return eip5792TransactionQuery.data?.receipts?.[0]?.blockNumber
      }
      return undefined
    })(),
    enabled: isBlockSyncEnabled && queryStatus === 'success',
  })

  const queryResult = args.type === 'regular' ? regularTransactionQuery : eip5792TransactionQuery

  if (isBlockSyncEnabled && queryStatus === 'success' && blockSync.isPending) {
    return {
      ...queryResult,
      data: undefined,
      isPending: true,
      isLoading: true,
      status: 'pending',
    } as UseWaitForTransactionReceiptUniversalResult<T>
  }

  return queryResult as UseWaitForTransactionReceiptUniversalResult<T>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function waitForTransactionReceiptQueryOptions(
  wagmiConfig: Config,
  { chainId, hash }: { chainId: number; hash: Hash | undefined },
) {
  return queryOptions({
    ...wagmi_waitForTransactionReceiptQueryOptions(wagmiConfig, {
      hash,
      chainId,
    }),
    enabled: !!hash,
  })
}

const CALLS_STATUS_REFETCH_INTERVAL = 1000
const CALLS_STATUS_QUERY_KEY_PREFIX = 'calls-status'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function callsStatusQueryOptions(wagmiConfig: Config, { callsId }: { callsId: string | undefined }) {
  return queryOptions<CallsStatusData>({
    queryKey: [CALLS_STATUS_QUERY_KEY_PREFIX, callsId],
    queryFn: callsId
      ? async () => {
          const callsStatus = await getCallsStatus(wagmiConfig, { id: callsId })

          if (callsStatus.status === 'pending') {
            return {
              status: 'pending',
            }
          }

          if (callsStatus.receipts?.some((receipt) => receipt.status === 'reverted')) {
            return {
              status: 'reverted',
              error: new Error('One of the transactions reverted'),
            }
          }

          return {
            status: 'success',
            receipts: callsStatus.receipts as WalletCallReceipt<bigint, 'success'>[],
          }
        }
      : skipToken,
    refetchInterval: (query) =>
      ['reverted', 'success'].includes(query.state.data?.status ?? '') ? false : CALLS_STATUS_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    enabled: !!callsId,
  })
}

function useIsMounted(): () => boolean {
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => isMounted.current, [])
}
